import { Flex, Text } from '@chakra-ui/react';
import { Button, Modal, useToast } from '@morressier/alembic';
import { ISubmissionContext } from '@publishing-engine/domain/models';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  useAddJournalArticleType,
  useUpdateJournalSettings
} from 'src/api/mutations';
import {
  fetchAvailableArticleTypes,
  fetchSubmissionContext
} from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import FieldAutoSuggestSelect from '../../../ArticleTypes/components/FieldSearchSelect';
import PeerReviewModelSelect from './PeerReviewModelSelect';
import {
  buildPeerReviewModelFieldConfig,
  PEER_REVIEW_MODEL_OPTIONS
} from './GeneralInfo/peerReviewModelConfig';

type AvailableArticleType = {
  id: string;
  name: string;
  description: string;
};

type AddArticleTypeFormValues = {
  articleType: AvailableArticleType | null;
};

/**
 * An article type that has been created but has no model yet, together with the
 * journal read back after its creation to find the workflow the model is stored
 * against. While this is set the modal asks for the model and nothing else.
 */
type PendingPeerReviewModel = {
  articleTypeName: string;
  articleWorkflowId: string;
  journal: ISubmissionContext;
};

/**
 * Adding an article type always ends by asking for its peer review model: an
 * active journal takes the article type live straight away, and an inactive one
 * cannot be activated until every article type has a model, so neither can be
 * left with a gap.
 */
const AddArticleTypeModal = () => {
  const [show, setShow] = useState(false);
  const [peerReviewModel, setPeerReviewModel] = useState('');
  const [pendingModelFor, setPendingModelFor] =
    useState<PendingPeerReviewModel | null>(null);
  const toast = useToast();
  const { journalId = '' } = useParams();
  const queryClient = useQueryClient();

  const { handleSubmit, reset, control, watch } =
    useForm<AddArticleTypeFormValues>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: { articleType: null }
    });

  const addJournalArticleTypeMutation = useAddJournalArticleType(journalId);
  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const selectedArticleType = watch('articleType');

  const { data: availableArticleTypesResponse, isLoading } = useQuery({
    queryKey: ['availableArticleTypes', journalId],
    queryFn: () => fetchAvailableArticleTypes(journalId),
    enabled: Boolean(journalId)
  });

  const articleTypes = availableArticleTypesResponse?.articleTypes ?? [];

  const articleTypeByName = useMemo(() => {
    const mapped = new Map<string, AvailableArticleType>();

    articleTypes.forEach(articleType => {
      mapped.set(articleType.name, articleType);
    });

    return mapped;
  }, [articleTypes]);

  const articleTypeNames = useMemo(
    () => articleTypes.map(articleType => articleType.name),
    [articleTypes]
  );

  const onClose = () => {
    setShow(false);
    setPendingModelFor(null);
    setPeerReviewModel('');
    reset({ articleType: null });
  };

  const onOpenModal = () => {
    setShow(true);
  };

  /**
   * The model is stored per article workflow and the workflow behind the new
   * article type only exists once it has been created, so the journal is read
   * back to find it. `undefined` means the article type is there but nothing can
   * be written against it.
   */
  const readCreatedArticleWorkflow = async (articleTypeId: string) => {
    try {
      const journal = await queryClient.fetchQuery({
        queryKey: ['submission-context', journalId],
        queryFn: () => fetchSubmissionContext(journalId)
      });

      const articleWorkflowId = journal.articleWorkflows?.[articleTypeId];

      return articleWorkflowId ? { journal, articleWorkflowId } : undefined;
    } catch {
      return undefined;
    }
  };

  const onCreateArticleType = async (values: AddArticleTypeFormValues) => {
    if (!values.articleType?.id) {
      toast({
        variant: 'error',
        title: 'Please select an article type'
      });
      return;
    }

    const response = await addJournalArticleTypeMutation.mutateAsync({
      articleTypeId: values.articleType.id
    });

    if (!response.isSuccess) {
      const message =
        typeof response.message === 'string'
          ? response.message
          : 'Failed to add article type';

      toast({
        variant: 'error',
        title: message
      });
      return;
    }

    const created = await readCreatedArticleWorkflow(values.articleType.id);

    if (!created) {
      /* Closed rather than left on a step with nothing to save against — the new
         row's edit icon takes the model from here. */
      toast({
        variant: 'error',
        title: `Added ${values.articleType.name}, but its peer review model could not be loaded. Set it from the article type's row.`
      });
      onClose();
      return;
    }

    setPendingModelFor({
      articleTypeName: values.articleType.name,
      articleWorkflowId: created.articleWorkflowId,
      journal: created.journal
    });
  };

  /**
   * The step this saves from offers no way out, because the article type is
   * already live on an active journal and cannot be left without a model. A save
   * that fails keeps the step open to be retried.
   */
  const onSavePeerReviewModel = async () => {
    if (!pendingModelFor || !peerReviewModel) {
      toast({
        variant: 'error',
        title: 'Please select a peer review model'
      });
      return;
    }

    try {
      const response = await updateSettingMutation.mutateAsync(
        buildPeerReviewModelFieldConfig(
          pendingModelFor.articleWorkflowId
        ).buildPayload(peerReviewModel, pendingModelFor.journal)
      );

      if (!response.isSuccess) {
        toast({
          variant: 'error',
          title: 'Could not save the peer review model'
        });
        return;
      }

      toast({
        variant: 'success',
        title: `Added ${pendingModelFor.articleTypeName}`
      });
      onClose();
    } catch (error: any) {
      toast({
        variant: 'error',
        title: error?.message || 'Could not save the peer review model'
      });
    }
  };

  const onSubmit = async (values: AddArticleTypeFormValues) =>
    pendingModelFor ? onSavePeerReviewModel() : onCreateArticleType(values);

  return (
    <>
      <Button onClick={onOpenModal}>Add Article Type</Button>

      {/* `onClose` is left inert and the close button hidden so the model step
          cannot be dismissed by the X, the overlay or Escape. */}
      <Modal
        isOpen={show}
        onClose={() => {}}
        width="600px"
        closeButtonProps={{
          hidden: true
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex direction={'column'} p={6}>
            <Text textStyle="headingSection">
              {pendingModelFor
                ? 'Select Peer Review Model'
                : 'Add Article Type'}
            </Text>
            {pendingModelFor ? (
              <Flex direction="column" gap={2} flex={1} my={5}>
                <Text textStyle="textBody">
                  <strong>{pendingModelFor.articleTypeName}</strong> has been
                  added. Select the peer review model it should review with to
                  finish.
                </Text>
                <PeerReviewModelSelect
                  value={peerReviewModel}
                  options={PEER_REVIEW_MODEL_OPTIONS}
                  onChange={setPeerReviewModel}
                />
              </Flex>
            ) : (
              <Flex direction="column" gap={2} flex={1} my={5}>
                <FieldLabel label="Article Type" subText="" isRequired />

                <Controller
                  name="articleType"
                  control={control}
                  rules={{ validate: value => Boolean(value?.id) }}
                  render={({ field }) => (
                    <FieldAutoSuggestSelect
                      value={field.value?.name ?? ''}
                      options={articleTypeNames}
                      placeholder={
                        isLoading
                          ? 'Loading article types...'
                          : 'Select article type'
                      }
                      noDataText={
                        isLoading
                          ? 'Loading article types...'
                          : 'No article types found'
                      }
                      isDisabled={
                        isLoading || addJournalArticleTypeMutation.isPending
                      }
                      onSelect={selectedName => {
                        field.onChange(
                          articleTypeByName.get(selectedName) ?? null
                        );
                      }}
                    />
                  )}
                />
              </Flex>
            )}
            <Flex justifyContent={'flex-end'} gap={4}>
              {pendingModelFor ? (
                <Button
                  type="submit"
                  isLoading={updateSettingMutation.isPending}
                  isDisabled={
                    !peerReviewModel || updateSettingMutation.isPending
                  }
                >
                  Save
                </Button>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    isDisabled={addJournalArticleTypeMutation.isPending}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    isLoading={addJournalArticleTypeMutation.isPending}
                    isDisabled={
                      isLoading ||
                      addJournalArticleTypeMutation.isPending ||
                      !selectedArticleType?.id
                    }
                  >
                    Add
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        </form>
      </Modal>
    </>
  );
};

export default AddArticleTypeModal;
